import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Container, Row, Col, Card, CardBody, FormGroup, Label, Input, Button, Alert } from 'reactstrap'
import { getIdData, addData, updateData } from '../../lib/booklist'
import { useRoleBasedUrl } from '../../Hooks/useRoleBasedUrl'

const AdminBookEdit = () => {
	const navigate = useNavigate()
	const { id, layout } = useParams()
	const role = useRoleBasedUrl()

	// Check if we're in create mode by looking at the current path
	const currentPath = window.location.pathname
	const isAddMode = currentPath.includes('/create/')

	const [loading, setLoading] = useState(false)
	const [error, setError] = useState(null)
	const [formData, setFormData] = useState({
		title: '',
		author: '',
		isActive: true
	})

	useEffect(() => {
		if (!isAddMode && id) {
			fetchBookData()
		}
	}, [id, isAddMode])

	const fetchBookData = async () => {
		if (!id || id === 'undefined') {
			return
		}

		try {
			setLoading(true)
			const bookData = await getIdData(id)
			if (bookData && bookData.length > 0) {
				setFormData({
					title: bookData[0].title || '',
					author: bookData[0].author || '',
					isActive: bookData[0].isActive || false
				})
			}
		} catch (err) {
			console.error('Error fetching book data:', err)
			setError('Failed to load book data')
		} finally {
			setLoading(false)
		}
	}

	const handleInputChange = (e) => {
		const { name, value, type, checked } = e.target
		setFormData((prev) => ({
			...prev,
			[name]: type === 'checkbox' ? checked : value
		}))
	}

	const handleSubmit = async (e) => {
		e.preventDefault()
		setLoading(true)
		setError(null)

		try {
			if (isAddMode) {
				await addData({
					title: formData.title,
					author: formData.author,
					isActive: formData.isActive
				})
			} else {
				await updateData({
					id: id,
					title: formData.title,
					author: formData.author,
					isActive: formData.isActive
				})
			}
			navigate(`${process.env.PUBLIC_URL}/${role}/books/${layout}`)
		} catch (err) {
			console.error('Error saving book:', err)
			setError('Failed to save book data')
		} finally {
			setLoading(false)
		}
	}

	const handleCancel = () => {
		navigate(`${process.env.PUBLIC_URL}/${role}/books/${layout}`)
	}

	if (loading && !isAddMode) {
		return (
			<Container fluid>
				<Row>
					<Col sm="12">
						<Card>
							<CardBody>
								<div className="text-center">
									<div className="spinner-border" role="status">
										<span className="visually-hidden">Loading...</span>
									</div>
									<p className="mt-2">Loading book data...</p>
								</div>
							</CardBody>
						</Card>
					</Col>
				</Row>
			</Container>
		)
	}

	return (
		<Container fluid>
			<Row>
				<Col sm="12">
					<Card>
						<CardBody>
							<div className="d-flex justify-content-between align-items-center mb-4">
								<h3>{isAddMode ? 'Add Book' : 'Update Book'}</h3>
							</div>

							{error && (
								<Alert color="danger" className="mb-4">
									{error}
								</Alert>
							)}

							<form onSubmit={handleSubmit}>
								<Row>
									<Col md="6">
										<FormGroup>
											<Label className="form-control-label">Title</Label>
											<Input
												type="text"
												name="title"
												value={formData.title}
												onChange={handleInputChange}
												placeholder="Enter book title"
												required
												disabled={loading}
											/>
										</FormGroup>
									</Col>
									<Col md="6">
										<FormGroup>
											<Label className="form-control-label">Author</Label>
											<Input
												type="text"
												name="author"
												value={formData.author}
												onChange={handleInputChange}
												placeholder="Enter author name"
												required
												disabled={loading}
											/>
										</FormGroup>
									</Col>
									<Col md="6">
										<FormGroup>
											<Label className="form-control-label">Status</Label>
											<div className="form-check form-switch">
												<Input type="checkbox" name="isActive" checked={formData.isActive} onChange={handleInputChange} disabled={loading} />
												<Label className="form-check-label">{formData.isActive ? 'Active' : 'Inactive'}</Label>
											</div>
										</FormGroup>
									</Col>
								</Row>

								<div className="d-flex justify-content-between mt-4">
									<Button type="submit" color="primary" disabled={loading}>
										{loading ? 'Saving...' : isAddMode ? 'Add Book' : 'Update Book'}
									</Button>
									<Button type="button" color="secondary" onClick={handleCancel} disabled={loading}>
										Cancel
									</Button>
								</div>
							</form>
						</CardBody>
					</Card>
				</Col>
			</Row>
		</Container>
	)
}

export { AdminBookEdit }
